import { createContext, useContext } from 'react'

import clsx from 'clsx'
import _ from 'lodash'

import { useDigitalProductContextOptional } from '@/shared/contexts/DigitalProductContext'
import { useLayoutChrome } from '@/shared/contexts/LayoutChromeContext'
import { useNavigation } from '@/shared/hooks/useNavigation'
import { useUserProgress } from '@/shared/hooks/useUserProgress'
import { readAppliedTenantIdentity } from '@/shared/lib/tenant/applied'
import { EventLogo, EVENT_LOGO_BOX_CLASS } from '@/shared/ui/EventLogo'
import { GameIcon, type GameIconName } from '@/shared/ui/GameIcon'
import { Link } from '@/shared/ui/Link'
import { ScoreCounter } from '@/shared/ui/ScoreCounter'
import { Skeleton } from '@/shared/ui/Skeleton'

import {
  resolveTopbarBackground,
  resolveTopbarSkin,
  resolveTopbarVariant,
  type TopbarSkin,
  type TopbarVariant
} from './topbarTheme'

export type { TopbarVariant, TopbarSkin } from './topbarTheme'

const TopbarVariantContext = createContext<TopbarVariant>('clean')
export const useTopbarVariant = () => useContext(TopbarVariantContext)

/**
 * Separado de `TopbarVariantContext`: skin (light/dark) e variant (clean/
 * immersive/themed) variam de forma independente — `themed` pode ser claro ou
 * escuro. Componentes fora do próprio `TopBar.tsx` (ex.: `AppMenuProfileTrigger`,
 * renderizado via slot `HeaderProfileArea`) precisam do skin para se adaptar,
 * não só da variant.
 */
const TopbarSkinContext = createContext<TopbarSkin>('light')
export const useTopbarSkin = () => useContext(TopbarSkinContext)

type WalletMetricProps = {
  icon: GameIconName
  value: number
  skin: TopbarSkin
  /** Cor do realce do bump — a da própria métrica exibida. */
  bumpClassName: string
}

/** Single metric row: 3D icon + value. Stacked vertically to keep the pill narrow. */
function WalletMetric({ icon, value, skin, bumpClassName }: WalletMetricProps) {
  const isDark = skin === 'dark'

  // `justify-between`: icon anchors left, value anchors right. Needed now that
  // the pill has a fixed `min-w` — without it, a 1-digit value left empty
  // space floating to its right instead of sitting at the pill's edge.
  //
  // O valor passa pelo `ScoreCounter` para que o ganho seja percebido: o saldo
  // sobe com count-up em vez de trocar de número em silêncio. O `w-full
  // justify-between` é reaplicado por `className` porque o componente é
  // `inline-flex` com `gap` — sem isso o pill volta a ter número flutuando no
  // meio com valor de 1 dígito.
  return (
    <div className="flex items-center justify-between gap-1.5">
      {/* `gray-800` no inset cinza da topbar: um tom mais escuro que `gray-700`
          sem chegar ao peso administrativo do `gray-900` ao lado dos ícones 3D.
          Na skin escura o numeral vira branco — o inset cinza dá lugar ao
          tratamento translúcido. */}
      <ScoreCounter
        value={value}
        size="sm"
        icon={<GameIcon name={icon} size={17} />}
        className="w-full justify-between"
        valueClassName="text-[14px] tracking-tight"
        restingColorClassName={isDark ? 'text-white' : 'text-gray-800'}
        bumpColorClassName={bumpClassName}
      />
    </div>
  )
}

type WalletProps = {
  coins: number
  href: string
  skin: TopbarSkin
}

function Wallet({ coins, href, skin }: WalletProps) {
  const isDark = skin === 'dark'

  return (
    <Link
      href={href}
      // `min-w`: calibrated so a 3-digit value (icon + gap + "999", tabular
      // nums) already fits inside the resting width — the pill must not grow
      // in a visibly perceptible way between "0" and low hundreds.
      // `rounded-lg`: mesmo raio do botão de perfil na topbar.
      //
      // A altura é o piso de alvo de toque, não o conteúdo: uma métrica só
      // mede 29px (ícone de 17px + `py-1.5`), abaixo dos 44px de
      // `--target-min`. Os 55px de antes seguravam duas métricas empilhadas e
      // aqui virariam folga sobrando. O `Skeleton` que espelha a pílula usa a
      // mesma altura — divergir reintroduz salto de layout.
      className={clsx(
        'gami-topbar-widget inline-flex h-[var(--target-min)] min-w-[72px] flex-col justify-center rounded-lg px-2 py-1.5',
        // O modificador nasceu no preset `immersive`, mas o tratamento
        // translúcido vale para toda skin escura — inclusive a `themed` dark.
        isDark && 'gami-topbar-widget--immersive'
      )}
      aria-label={`${coins} moedas`}
    >
      {/* Moeda com a cor da própria métrica: o `ScoreCounter` não traz cor de
          bump default, justamente para não pintar a métrica errada. */}
      <WalletMetric
        icon="coins"
        value={coins}
        skin={skin}
        bumpClassName="text-[var(--gami-coin-color)]"
      />
    </Link>
  )
}

type LayoutHeaderProps = {
  variant?: TopbarVariant
}

export const LayoutHeader = ({ variant: variantProp }: LayoutHeaderProps) => {
  // Optional porque a topbar monta ANTES do evento: é ela que segura a altura
  // do chrome desde o primeiro frame, para o conteúdo abaixo não saltar quando
  // nome, logo e avatar entram.
  const contexto = useDigitalProductContextOptional()
  const digitalProduct = contexto?.digitalProduct
  const carregando = !contexto?.digitalProduct
  const { wallet } = useUserProgress()
  const { basePathWithId } = useNavigation()
  const { HeaderProfileArea } = useLayoutChrome()

  // Valor de PARTIDA da barra, não um segundo dado: enquanto o evento
  // confirmado não chega, a identidade do bootstrap responde pela superfície,
  // e quando ele chega com o mesmo modo nada repinta. Sem isso a barra nasce
  // branca e escurece aos oito segundos.
  const identidadeAplicada = readAppliedTenantIdentity()

  const topbarMode =
    digitalProduct?.topbar_mode ?? identidadeAplicada?.topbar_mode ?? null
  // A prop continua valendo como override em código; o dado do evento é só o
  // default. Sem `topbar_mode` configurado o resolver devolve `clean`, que é o
  // comportamento de hoje.
  const variant = variantProp ?? resolveTopbarVariant(topbarMode)
  const skin = resolveTopbarSkin(variant, topbarMode)
  const isDark = skin === 'dark'
  const isImmersive = variant === 'immersive'
  const themedBackground = resolveTopbarBackground({
    variant,
    mode: topbarMode,
    textureUrl:
      _.get(digitalProduct, 'topbar_texture.file.url') ??
      identidadeAplicada?.topbarTextureUrl,
    colorLight:
      digitalProduct?.topbar_color_light ??
      identidadeAplicada?.topbar_color_light,
    colorDark:
      digitalProduct?.topbar_color_dark ??
      identidadeAplicada?.topbar_color_dark
  })

  const coins = Number(wallet.coins ?? 0)
  const rewardsHref = `${basePathWithId}/extrato-de-recompensas`
  const logoSrc =
    _.get(digitalProduct, 'logo.file.url') ||
    identidadeAplicada?.logoUrl ||
    (isDark ? '/logo-white.png' : '/logo-dark-alt.png')

  return (
    <TopbarVariantContext.Provider value={variant}>
      <TopbarSkinContext.Provider value={skin}>
        <header
          className={clsx(
            'relative z-[3] h-[var(--gami-topbar-h,72px)] w-full',
            // `bg-cover`/`bg-center` são exclusivos do preset `immersive`: em
            // `themed` eles anulariam o `background-repeat` da textura do evento.
            isImmersive && 'bg-brand bg-cover bg-center shadow-md',
            variant === 'themed' && 'shadow-sm',
            variant === 'clean' && 'bg-white shadow-sm',
            isDark && 'text-white'
          )}
          style={
            isImmersive
              ? { backgroundImage: 'url(/bgs/topbar.jpg)' }
              : themedBackground
          }
        >
          <div className="mx-auto flex h-full w-full max-w-2xl items-center justify-between gap-3 px-3 md:px-4">
            <div
              className="flex min-w-0 grow items-center"
              style={{ flexBasis: 0 }}
            >
              {carregando ? (
                <Skeleton
                  className="h-[var(--target-min)] w-[72px]"
                  variant="default"
                />
              ) : (
                <Wallet coins={coins} href={rewardsHref} skin={skin} />
              )}
            </div>

            <div className="flex shrink-0 items-center">
              {carregando ? (
                // Mesma caixa do logo — placeholder em vez de omissão, senão a
                // topbar muda de altura quando a imagem entra.
                <Skeleton
                  className={clsx(EVENT_LOGO_BOX_CLASS.topbar, 'rounded-md')}
                  variant="thumb"
                />
              ) : (
                <Link href="/">
                  <EventLogo
                    id="logo"
                    alt="Logo"
                    src={logoSrc}
                    variant="topbar"
                  />
                </Link>
              )}
            </div>

            <div
              className="flex min-w-0 grow items-center justify-end"
              style={{ flexBasis: 0 }}
            >
              {carregando ? (
                <Skeleton className="size-10 md:size-11" variant="circle" />
              ) : HeaderProfileArea ? (
                <HeaderProfileArea />
              ) : null}
            </div>
          </div>
        </header>
      </TopbarSkinContext.Provider>
    </TopbarVariantContext.Provider>
  )
}
